import React, { useMemo } from 'react'
import { Box, Paper, Typography, Chip, Grid, IconButton } from '@mui/material'
import { AlertCard } from './AlertCard'
import { DocumentsList } from './DocumentsList'
import { AlertsList } from './AlertsList'
import { formatDate, formatDateRangeEnd } from '../_helpers'
import { useDashboardAlertTotals } from '../../../../queries/hooks/useDashboardAlertTotals'
import useDashboardListFilters from '../../../../util/useDashboardListFilters'
import { useDashboardFilterOptions } from '../../../../queries/hooks/useDashboardFilterOptions'
import { useDashboardTableDocuments } from '../../../../queries/hooks/useDashboardTableDocuments'
import { useResetDashboardPage } from '../_hooks'

const PatientDetailView: React.FC = () => {
  const { filters, updateFilters } = useDashboardListFilters()
  const { data: filterOptions } = useDashboardFilterOptions()
  const { alertTotals: alertCounts, isLoading: isLoadingAlertCounts } = useDashboardAlertTotals()
  const {
    data: patientDocumentsData,
    isLoading: isPatientDocumentsLoading,
    error: patientDocumentsError,
    currentPage: patientDocumentsPage,
    setCurrentPage: setPatientDocumentsPage,
  } = useDashboardTableDocuments({
    rowsPerPage: 5,
    sortBy: 'documentAt',
    sortOrder: 'desc',
  })
  useResetDashboardPage({ filters, setCurrentPage: setPatientDocumentsPage })
  const selectedPatient = filterOptions?.patients.find(
    (patient) => patient.mrn === filters.detailViewPatientMrn
  )
  const patientName = selectedPatient?.name
  const clinicians = useMemo(
    () =>
      Array.from(
        new Set(
          (patientDocumentsData?.results ?? [])
            .map((document) => document.clinicianName)
            .filter((name): name is string => !!name)
        )
      ),
    [patientDocumentsData?.results]
  )

  const handleClose = () => {
    updateFilters({ detailViewPatientMrn: null })
  }

  return (
    <Paper elevation={0} sx={{ p: 3, mb: 4, position: 'relative' }}>
      <IconButton
        onClick={handleClose}
        sx={{ position: 'absolute', top: 10, right: 10 }}
        aria-label="close"
        data-qa-id="patient-details-close-button"
      >
        ✕
      </IconButton>

      <Typography variant="h5" gutterBottom>
        Patient Details
      </Typography>

      {patientName ? (
        <>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6">{patientName}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Date range: {formatDate(filters.selectedDateRange?.from ?? '')} -{' '}
              {formatDateRangeEnd(filters.selectedDateRange?.to ?? '')}
            </Typography>
          </Box>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <AlertCard severity={1} count={alertCounts.low} isLoading={isLoadingAlertCounts} />
            <AlertCard severity={2} count={alertCounts.medium} isLoading={isLoadingAlertCounts} />
            <AlertCard severity={3} count={alertCounts.critical} isLoading={isLoadingAlertCounts} />
          </Grid>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Clinicians ({clinicians.length})
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {clinicians.map((name) => (
                <Chip key={name} label={name} />
              ))}
            </Box>
          </Box>

          <DocumentsList
            data={patientDocumentsData}
            isLoading={isPatientDocumentsLoading}
            error={patientDocumentsError}
            currentPage={patientDocumentsPage}
            setCurrentPage={setPatientDocumentsPage}
          />
          <AlertsList />
        </>
      ) : (
        <Typography>No data available for this patient.</Typography>
      )}
    </Paper>
  )
}

export default PatientDetailView
